import React from 'react';
import { View, Text, StyleSheet, Image, TouchableOpacity, Platform } from 'react-native';
import { MdDirectionsTransit } from 'react-icons/md';
import ItineraryItemCard from './ItineraryItemCard';
import PlatformIcon from '../common/PlatformIcon';
import { ORANGE_ACTION } from '../../constants/colors';

export interface ActivityCardProps {
  title: string;
  note?: string;
  time?: string;
  venue?: string;
  startTime?: string;
  endTime?: string;
  actionIcon?: React.ReactNode;
  transportNote?: string;
  imageUrl?: string;
  /** 地址（遊記模式） */
  address?: string;
  /** 電話（遊記模式） */
  phone?: string;
}

const ActivityCard: React.FC<ActivityCardProps> = ({ title, note, venue, startTime, endTime, actionIcon, transportNote, imageUrl, address, phone }) => {
  const [noteExpanded, setNoteExpanded] = React.useState(false);
  const shouldShowToggle = (() => {
    if (!note) return false;
    try {
      const len = String(note).length;
      // 中文字符在窄屏幕上 2 行大約只能顯示 40-60 個字符
      // 降低閾值以確保被截斷的文字能顯示展開按鈕
      if (len >= 50) return true;
      if (String(note).includes('\n')) return true;
      if ((String(note).match(/\s+/g) || []).length > 8) return true;
      return false;
    } catch {
      return false;
    }
  })();
  const cardBody = (
    <>
      <View style={styles.rowTop}>
        <Text style={styles.title}>{title}</Text>
        <View style={styles.actions}>
          {actionIcon ? actionIcon : <PlatformIcon name="event" size={16} color={ORANGE_ACTION} />}
        </View>
      </View>
      <View style={styles.metaRow}>
        {venue ? <Text style={styles.metaText}>{venue}</Text> : null}
        {startTime || endTime ? (
          <Text style={styles.metaText}>{(startTime || '') + (endTime ? ` - ${endTime}` : '')}</Text>
        ) : null}
      </View>
      {/* 地址（遊記模式） */}
      {address ? (
        <View style={styles.infoRow}>
          <View style={styles.iconWrapper}>
            <PlatformIcon name="place" size={16} color="#9CA3AF" />
          </View>
          <Text style={styles.infoText} numberOfLines={2}>{address}</Text>
        </View>
      ) : null}
      {/* 電話（遊記模式） */}
      {phone ? (
        <View style={styles.infoRow}>
          <View style={styles.iconWrapper}>
            <PlatformIcon name="call" size={16} color="#9CA3AF" />
          </View>
          <Text style={styles.infoText}>{phone}</Text>
        </View>
      ) : null}
      {transportNote ? (
        <View style={styles.infoRow}>
          <View style={styles.iconWrapper}>
            {React.createElement(MdDirectionsTransit as any, { size: 16, color: '#9CA3AF' })}
          </View>
          <Text style={styles.infoText} numberOfLines={3}>{transportNote}</Text>
        </View>
      ) : null}
      {note ? (
        <>
          <Text style={styles.note} numberOfLines={noteExpanded ? undefined : 2}>{note}</Text>
          {shouldShowToggle ? (
            <TouchableOpacity style={styles.noteToggleButton} onPress={() => setNoteExpanded(v => !v)}>
              <Text style={styles.noteToggleText}>{noteExpanded ? '收起 ▴' : '展开 ▾'}</Text>
            </TouchableOpacity>
          ) : null}
        </>
      ) : null}
    </>
  );

  if (imageUrl) {
    return (
      <View style={styles.cardContainer}>
        <View style={styles.imageWrapper}>
          <Image source={{ uri: imageUrl }} style={styles.image} resizeMode="cover" />
        </View>
        <View style={styles.cardBody}>
          {cardBody}
        </View>
      </View>
    );
  }

  return (
    <ItineraryItemCard>
      {cardBody}
    </ItineraryItemCard>
  );
};

const styles = StyleSheet.create({
  cardContainer: {
    backgroundColor: '#fff',
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    overflow: 'hidden',
    ...Platform.select({
      web: {
        boxShadow: '0px 8px 20px rgba(0,0,0,0.06)'
      },
      default: {
        shadowColor: '#000',
        shadowOpacity: 0.06,
        shadowOffset: { width: 0, height: 8 },
        shadowRadius: 20,
        elevation: 2,
      },
    }),
  },
  imageWrapper: {
    width: '100%',
    aspectRatio: 16 / 9,
    backgroundColor: '#F3F4F6',
  },
  image: {
    width: '100%',
    height: '100%',
  },
  cardBody: {
    paddingVertical: 14,
    paddingHorizontal: 12,
    backgroundColor: '#fff',
  },
  rowTop: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  title: { fontSize: 16, fontWeight: '600', color: '#111827', flex: 1 },
  actions: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', marginLeft: 8, height: 20 },
  metaRow: { flexDirection: 'row', alignItems: 'center', marginTop: 6 },
  metaText: { marginRight: 10, color: '#6B7280' },
  infoRow: { flexDirection: 'row', alignItems: 'flex-start', marginTop: 6, gap: 8 },
  iconWrapper: { width: 16, height: 16, justifyContent: 'center', alignItems: 'center', flexShrink: 0, marginTop: 2 },
  infoText: { color: '#6B7280', flex: 1, lineHeight: 18, fontSize: 13 },
  note: { marginTop: 8, color: '#6B7280' },
  noteToggleButton: { marginTop: 8, paddingVertical: 10, borderWidth: 1, borderColor: '#E5E7EB', borderRadius: 12, alignItems: 'center', justifyContent: 'center' },
  noteToggleText: { marginTop: 0, fontSize: 14, color: ORANGE_ACTION },
});

export default ActivityCard;
